import { useState, useEffect, useCallback, useRef } from "react";
import {
  X,
  Search,
  Share2,
  Hash,
  Users,
  MessageCircle,
  Loader2,
  Check,
} from "lucide-react";
import { getConversations } from "../../../api/teamChat";
import { createShare } from "../../../api/sharing";
import { shareArticle } from "../../../api/sharing";
import { useTeamPresence } from "../../contexts/TeamPresenceContext";

const ShareModal = ({
  onClose,
  contentType,
  documentId,
  companyName,
  itemName,
  articleSnapshot,
}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [sharing, setSharing] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const searchRef = useRef(null);
  const { socket } = useTeamPresence();

  useEffect(() => {
    setTimeout(() => searchRef.current?.focus(), 100);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getConversations();
        const d = res?.data || {};
        setConversations([
          ...(d.channels || []).map((c) => ({ ...c, _kind: "channel" })),
          ...(d.groups || []).map((c) => ({ ...c, _kind: "group" })),
          ...(d.dms || []).map((c) => ({ ...c, _kind: "dm" })),
        ]);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const nm = (c) =>
    c._kind === "dm" ? c.dm_display_name || "DM" : c.name || "Conv";
  const ic = (c) =>
    c._kind === "channel" ? Hash : c._kind === "group" ? Users : MessageCircle;
  const lb = (c) =>
    c._kind === "channel"
      ? "Channel"
      : c._kind === "group"
        ? "Group"
        : "Direct message";
  const tt = (c) =>
    c._kind === "channel" ? "general" : c._kind === "group" ? "group" : "dm";

  const filtered = conversations.filter(
    (c) => !query.trim() || nm(c).toLowerCase().includes(query.toLowerCase()),
  );

  const handleShare = useCallback(async () => {
    if (!selected) return;
    setSharing(true);
    setError("");
    try {
      let res;
      if (contentType === "article") {
        res = await shareArticle(
          documentId,
          tt(selected),
          selected.team_id,
          message.trim(),
          articleSnapshot || {},
        );
      } else {
        res = await createShare(
          contentType,
          documentId,
          tt(selected),
          selected.team_id,
          message.trim(),
          companyName || "",
        );
      }

      const data = res?.data || {};
      if (data.status === 200 && data.message && socket?.current) {
        socket.current.emit("broadcast_share", {
          team_id: selected.team_id,
          message: data.message,
          recipient_uuids: data.recipient_uuids || [],
        });
      }

      setDone(true);
      setTimeout(() => onClose(), 1200);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to share");
    }
    setSharing(false);
  }, [
    selected,
    contentType,
    documentId,
    companyName,
    message,
    onClose,
    socket,
    articleSnapshot,
  ]);

  return (
    <div
      className="w-[420px] max-h-[520px] flex flex-col bg-popUpbg/90 backdrop-blur-lg rounded-lg shadow-login-panel border border-surfaceBorder overflow-hidden font-poppins font-normal text-mainText"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-mainDivider">
        <div className="flex items-center gap-x-2">
          <Share2 className="h-4 w-4 text-velBlue" strokeWidth={2} />
          <h3 className="text-sm font-normal text-mainText">Share</h3>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg border border-transparent bg-transparent p-1 text-mutedText transition hover:bg-btnHover hover:text-mainText outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 cursor-pointer"
          aria-label="Close"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {/* Item info bar */}
      <div className="px-4 py-2.5 border-b border-mainDivider bg-innerBg">
        <p className="text-[11px] font-normal text-mutedText uppercase tracking-wide">
          Sharing
        </p>
        <p className="text-[13px] text-mainText truncate mt-0.5">
          {itemName || "Selected item"}
        </p>
      </div>

      {done ? (
        <div className="flex-1 flex flex-col items-center justify-center py-12 gap-y-3">
          <div className="w-10 h-10 rounded-full bg-successDot/10 flex items-center justify-center">
            <Check className="h-5 w-5 text-successDot" strokeWidth={2} />
          </div>
          <p className="text-[13px] text-mutedText">Shared to {nm(selected)}</p>
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="px-4 pt-3 pb-2">
            <div className="relative">
              <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mutedText">
                <Search className="h-3.5 w-3.5" strokeWidth={2} />
              </div>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search conversations..."
                className="w-full rounded-lg border border-inputBorder bg-transparent pl-9 pr-3 py-2.5 text-sm text-mainText outline-none ring-inputFocus/60 transition focus:ring-2 placeholder:text-mutedText"
              />
            </div>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto px-2 pb-2 min-h-0">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2
                  className="h-5 w-5 text-mutedText animate-spin"
                  aria-hidden
                />
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-2 py-4 text-center text-xs text-mutedText">
                {query.trim()
                  ? "No conversations found"
                  : "No conversations yet"}
              </p>
            ) : (
              filtered.map((conv) => {
                const I = ic(conv),
                  n = nm(conv),
                  l = lb(conv),
                  sel = selected?.team_id === conv.team_id;
                return (
                  <button
                    key={conv.team_id}
                    onClick={() => setSelected(conv)}
                    disabled={sharing}
                    className={[
                      "w-full flex items-center gap-x-3 px-3 py-2 rounded-lg text-left transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
                      sel
                        ? "bg-selectedSection border border-surfaceBorder"
                        : "border border-transparent hover:bg-mainButtonBg/50",
                      sharing ? "opacity-50 cursor-not-allowed" : "",
                    ].join(" ")}
                  >
                    <span className="w-8 h-8 rounded-full bg-mainButtonBg flex items-center justify-center shrink-0">
                      <I
                        className="h-3.5 w-3.5 text-mutedText"
                        strokeWidth={2}
                      />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-normal text-mainText truncate">
                        {n}
                      </p>
                      <p className="text-xs text-mutedText">{l}</p>
                    </div>
                    {sel && (
                      <span className="w-5 h-5 rounded-full bg-velBlue flex items-center justify-center shrink-0">
                        <Check
                          className="h-3 w-3 text-copyText"
                          strokeWidth={3}
                        />
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {selected && (
            <div className="px-4 py-3 border-t border-mainDivider space-y-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a message (optional)"
                className="w-full rounded-lg border border-inputBorder bg-transparent px-3 py-2.5 text-sm text-mainText outline-none ring-inputFocus/60 transition focus:ring-2 placeholder:text-mutedText"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleShare();
                  }
                }}
              />
              {error && <p className="text-xs text-errorText">{error}</p>}
              <button
                onClick={handleShare}
                disabled={sharing}
                className="w-full flex items-center justify-center gap-x-2 rounded-lg bg-mainButtonBg px-4 py-2.5 text-sm font-normal text-mainText transition hover:bg-secondaryButtonBg outline-none ring-inputFocus/60 focus-visible:ring-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {sharing ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Share2 className="h-4 w-4" strokeWidth={2} aria-hidden />
                )}
                Share to {nm(selected)}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShareModal;
